/**
 * StoreLinkButton — shared presentational anchor for app-store buttons.
 * Internal base for {@link AppStoreButton} / {@link PlayStoreButton}; not exported.
 *
 * @module butterUI/domains/app/StoreLinkButton
 */

import type { ReactNode } from 'react';
import { cn } from '#utils';

export interface StoreLinkButtonProps {
  /** Destination store URL. */
  href: string;
  /** Store glyph (Apple / Google Play). */
  icon: ReactNode;
  /** Small line above the store name, e.g. "Download on the". */
  topLine: string;
  /** Store name, e.g. "App Store". */
  bottomLine: string;
  className?: string;
}

export function StoreLinkButton({
  href,
  icon,
  topLine,
  bottomLine,
  className,
}: StoreLinkButtonProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center justify-center gap-3 rounded-2xl bg-black px-6 py-4 text-white transition-colors',
        'text-interactive:bg-gray-800',
        // brand ring per StyleAndA11y.mdx — also the only visible option here,
        // since a black ring on a black button reads as no focus indicator.
        'focus:outline-none focus:ring-2 focus:ring-butter-brand-600 focus:ring-offset-2',
        className,
      )}
    >
      {icon}
      <div className="text-left">
        <div className="text-xs opacity-80">{topLine}</div>
        <div className="text-lg font-semibold">{bottomLine}</div>
      </div>
    </a>
  );
}
